import React, { useState } from 'react';
import {
  Alert, Button, Metric, XClose,
} from '@opendate-app/setlist';
import {
  buildKpiCards, buildKpiCatalog, MAX_KPI_CARDS, MIN_KPI_CARDS,
} from '../../utils/dashboardUtils';
import KpiPicker from './KpiPicker';
import { KpiCardSkeleton } from '../Skeletons';
import { gridStyle } from './layout';
import '../../styles/reportingStyles.css';

const YOY_COLORS = { up: 'var(--sl-text-success)', down: 'var(--sl-text-error)', flat: 'var(--sl-text-tertiary)' };
const YOY_ARROWS = { up: '↑', down: '↓', flat: '→' };

function YoyLine({ change }) {
  return (
    <div style={{ fontSize: 12 }}>
      <span style={{ fontWeight: 600, color: YOY_COLORS[change.direction] }}>{YOY_ARROWS[change.direction]} {change.value}</span>
      <span style={{ color: 'var(--sl-text-tertiary)' }}> {change.label}</span>
    </div>
  );
}

const FOOTER_STACK_STYLE = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, marginLeft: 'auto' };

function Caption({ text }) {
  return <span style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{text}</span>;
}

const SHOW_DATE_NOTE = 'Sell-through, check-in rate, per-cap, and show profit are always counted by event date, including sell-through by venue.';
const PURCHASE_DATE_NOTE = 'Memberships, packs & passes, classes, donations, and resale fees always count by purchase date.';

function DragHandle() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      {[6, 12, 18].map((y) => [9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
    </svg>
  );
}

function EditBanner({ count }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 16, borderRadius: 12,
        border: '1px solid var(--sl-border-brand)', background: 'var(--sl-bg-brand-primary)', fontSize: 14, color: 'var(--sl-text-secondary)',
      }}
    >
      <span style={{ color: 'var(--sl-fg-brand)' }}><DragHandle /></span>
      <div style={{ flex: 1 }}>
        <span style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>Customizing your KPI cards. </span>
        Drag to reorder, × to remove, + to add. Only you see this layout.
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sl-text-brand)', whiteSpace: 'nowrap' }}>{`${count} of ${MAX_KPI_CARDS} cards`}</span>
    </div>
  );
}

function EditableCard({
  cardKey, dragKey, setDragKey, onMove, onRemove, canRemove, children,
}) {
  const dragging = dragKey === cardKey;
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', cardKey); setDragKey(cardKey); }}
      onDragOver={(e) => { if (!dragKey) return; e.preventDefault(); if (dragKey !== cardKey) onMove(dragKey, cardKey); }}
      onDrop={(e) => e.preventDefault()}
      onDragEnd={() => setDragKey(null)}
      style={{
        position: 'relative', cursor: 'grab', borderRadius: 12, opacity: dragging ? 0.45 : 1,
        outline: `1px dashed ${dragging ? 'var(--sl-border-brand)' : 'var(--sl-border-primary)'}`, outlineOffset: 2,
      }}
    >
      {children}
      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span title="Drag to reorder" style={{ color: 'var(--sl-fg-quaternary)', display: 'inline-flex' }}><DragHandle /></span>
        <Button
          variant="tertiary"
          size="sm"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Remove card"
          title={canRemove ? 'Remove card' : `Keep at least ${MIN_KPI_CARDS} cards`}
        >
          <XClose width={16} height={16} />
        </Button>
      </div>
    </div>
  );
}

function KpiRow({
  data, loading, error, comparisonLabel, isMobile, dateMode, yearOverYear, links = {}, keys, editing = false, onChange,
}) {
  const [dragKey, setDragKey] = useState(null);
  const style = gridStyle(isMobile, 'repeat(3, minmax(0, 1fr))');
  const options = { dateMode, yearOverYear };

  const move = (from, to) => {
    const next = keys.filter((k) => k !== from);
    next.splice(keys.indexOf(to), 0, from);
    onChange(next);
  };
  const toggle = (key) => onChange(keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]);

  const renderMetric = (card) => (
    <Metric
      label={links[card.key] && !editing ? <a href={links[card.key]} style={{ color: 'inherit' }}>{card.label} →</a> : card.label}
      value={<span className="kpi-row-value">{card.value}</span>}
      change={card.change}
      footer={card.yoyChange ? (
        <div style={FOOTER_STACK_STYLE}>
          <YoyLine change={card.yoyChange} />
          <Caption text={card.caption} />
        </div>
      ) : <Caption text={card.caption} />}
    />
  );

  const cards = loading
    ? keys.map((key) => <KpiCardSkeleton key={key} showBottom={false} />)
    : buildKpiCards(data || {}, comparisonLabel, options, keys).map((card) => (editing ? (
      <EditableCard
        key={card.key}
        cardKey={card.key}
        dragKey={dragKey}
        setDragKey={setDragKey}
        onMove={move}
        onRemove={() => toggle(card.key)}
        canRemove={keys.length > MIN_KPI_CARDS}
      >
        {renderMetric(card)}
      </EditableCard>
    ) : <React.Fragment key={card.key}>{renderMetric(card)}</React.Fragment>));

  return (
    <>
      {editing && <EditBanner count={keys.length} />}
      {error && !loading ? (
        <div style={{ marginBottom: 16 }}>
          <Alert color="error" title="Couldn't load KPIs" description="Refresh the page to try again." />
        </div>
      ) : (
        <div className="kpi-row" style={style}>
          {cards}
          {editing && !loading && keys.length < MAX_KPI_CARDS && (
            <KpiPicker catalog={buildKpiCatalog(data || {}, comparisonLabel, options)} keys={keys} onToggle={toggle} />
          )}
        </div>
      )}
      {editing && keys.length >= MAX_KPI_CARDS && (
        <p style={{ fontSize: 13, color: 'var(--sl-text-tertiary)', marginTop: -8 }}>
          {`You’re at the ${MAX_KPI_CARDS}-card limit. Remove a card to add another.`}
        </p>
      )}
      {dateMode === 'order_date' && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="gray" description={SHOW_DATE_NOTE} />
        </div>
      )}
      {dateMode === 'event_date' && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="gray" description={PURCHASE_DATE_NOTE} />
        </div>
      )}
    </>
  );
}

export default KpiRow;
